import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IconButton } from "@mui/material";
import Text from "../../components/Text";
import { useState, useEffect } from "react";
import TextContainer from "../../components/TextContainer";
import Button from "@mui/material/Button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInLink } from "../../scripts/functions/AuthService";
import Spinner from "../../components/Spinner";
import SessionScreen from "../../components/SessionScreen";
import { useAuth } from '../../scripts/functions/AuthProvider';
import RestorePassword from '../../components/RestorePassword';
import { useTheme } from '../../scripts/functions/ThemeProvider';
import { AccessReqHandler } from '../../scripts/functions/AccessReqHandler';
import { AccessValidations } from '../../scripts/functions/AccessValidations';
import { loadFromStorage, useSaveToStorage } from '../../utils';

const Login = () => {
    const persisted = loadFromStorage('SESSION_DATA') || {};
    const {refreshAuthState, isAuthenticated, isLoading: authLoading} = useAuth();
    const {theme, isDark} = useTheme();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [generalError, setGeneralError] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [tempCode, setTempCode] = useState(null);
    const [mailAction, setMailAction] = useState(false);
    const {validateToken} = AccessReqHandler();

    const {mail, password, formError, isValid, setMail, setPassword, setFormError, validateFields} = AccessValidations(persisted.mail);

    useSaveToStorage('SESSION_DATA', {mail, name: persisted.name});
    const navigate = useNavigate();

    useEffect(() => {
        const token = searchParams.get("token");
        // console.log(`Token: ${token}`)

        if (!token || mailAction) return;

        setMailAction(true);
        setIsLoading(true);
        validateToken(token).then(data => {
            if (data && data.email) {
                setMail(data.email);
                setTempCode(token);
                openPopup("RestorePassword");
            } else {
                console.error("Token inválido")
                setGeneralError("El enlace ha expirado");
            }

        }).catch(err => {
            console.error("Error en validación", err);
            setMailAction(false);
        })
    }, [searchParams, mailAction])

    const openPopup = (popup) => {
        setIsPopupOpen(popup);
    }

    const closePopup = () => {
        setIsPopupOpen(null);
        setSearchParams({});
    }

    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate('/produccion/dashboard');
        }
    }, [authLoading, isAuthenticated, navigate]);

    const access = () => {
        navigate('/administracion/request-access')
    }

    const login = async (e, email, pass) => {
        if (e) e.preventDefault();

        setGeneralError(null);

        const currentMail = email || mail;
        const currentPass = pass || password;

        const {errors, isValid} = validateFields(currentMail, currentPass);
        setFormError(errors);

        if(!isValid) {
            // console.log("Entered");
            return;
        }

        setIsLoading(true);

        try {
            const session = await signInLink(currentMail, currentPass);
            
            if (session.isSignedIn){
                // console.log("Session detected, redirecting to dashboard");
                localStorage.removeItem('SESSION_DATA') //Delete the login cache if the login is successful
                await refreshAuthState();
                navigate('/produccion/dashboard');
            }            
        } catch(err) {
            if(err.name === "UserAlreadyAuthenticatedException") {
                console.log("Ya hay una sesión iniciada, redirigiendo al dashboard");
                navigate('/produccion/dashboard');
            } else {
                setGeneralError("Usuario o contraseña incorrectas. Verifique su información")
                console.error("Error al iniciar sesión: ", err);
            }
        } finally {
            setIsLoading(false);
        }
    }

    if (authLoading) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', position: 'relative'}}>
            {
                isLoading && (
                    <div style={{position: 'absolute', top:0, left:0, right:0, bottom:0, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <Spinner theme={theme}/> 
                    </div>
                )
            }
            </div>
        )
    }
    return(
        <SessionScreen theme={theme} isDark={isDark}>
            <form onSubmit={login}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start', width: '100%', gap: 4, position: 'relative'}}>
                    <Text color={theme.Black} size={15} weight={"medium"}>Correo</Text>
                    <TextContainer autoComplete="username" style={{width: '100%', height: 36}} value={mail} theme={theme} placeHolder="something@domain.com.mx" onChange={(value) => setMail(value)} />
                    {formError.mail && <Text color="red" size={12} style={{ top: 65, position: 'absolute' }}>{formError.mail}</Text>}
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start', width: '100%', gap: 4, position: 'relative', marginTop: '30px'}}>
                    <Text color={theme.Black} size={15} weight={"medium"}>Contraseña</Text>
                    <TextContainer type={showPassword ? "text" : "password"} autoComplete="current-password" style={{width: '100%', height: 36}} value={password} theme={theme} placeHolder="········" onChange={(value) => setPassword(value)} endIcon={<IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" style={{color: theme.Lao}}>{showPassword ? <VisibilityOff/> : <Visibility/>}</IconButton>}/>
                    {formError.password && <Text color="red" size={12} style={{ top: 65, position: 'absolute' }}>{formError.password}</Text>}
                    {generalError && !formError.password && <Text color="red" size={12} style={{top: 65, position: 'absolute' }}>{generalError}</Text>}
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start', width: '100%', marginTop:'30px'}}>
                    <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={theme.Magenta} onClick={() => openPopup("RestorePassword")} size={15}>Recuperar contraseña</Text>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start', width: '100%', marginTop:'30px'}}>
                    <Button disableElevation type="submit" disabled={!isValid} variant="contained" sx={{width: '100%', height: 52, border: 1, borderColor: theme.Coco, backgroundColor: isValid ? theme.Black : theme.Ey, color: isValid ? theme.White : theme.Audi, fontWeight: 'medium', fontSize: 15, textTransform: 'none', '&.Mui-disabled': {backgroundColor: isDark ? theme.Audi : theme.Ey}}}>Iniciar sesión</Button>            
                </div>
            </form>
            <div style={{display: 'flex', flexDirection: 'column', position: 'relative'}}>
            {
                isLoading && (
                    <div style={{position: 'absolute', top:0, left:0, right:0, bottom:0, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <Spinner theme={theme}/> 
                    </div>
                )
            }
            </div>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start', width: '100%', gap: 8, marginTop: 'clamp(24px, 10dvh, 128px)'}}>
                <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={theme.Magenta} onClick={() => access()} size={15}>Solicitar acceso</Text>
            </div>

            {isPopupOpen === "RestorePassword" && <RestorePassword theme={theme} onClose={closePopup} onSuccess={(newPassword) => {closePopup(); login(null, mail, newPassword)} } initialMail={mail} tempCode={tempCode} isDark={isDark}/>}
        </SessionScreen>
    )
}

export default Login;